import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
// Tour removed
import {
  LayoutDashboard,
  Settings,
  Package,
  ShoppingCart,
  ShoppingBag,
  FileText,
  LogOut,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  Store,
  Settings2,
  Layers,
  Zap,
  Activity,
  Plus,
  Minus,
  PackagePlus,
  Image,
  DollarSign,
  Truck,
  BarChart3,
  Server,
  Globe,
  Sparkles,
  Webhook,
  Code,
  Database,
  ArrowLeftRight,
  AlertTriangle,
  HelpCircle,
} from "lucide-react";
import { useState } from "react";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard", exact: true },
  {
    path: "/magazalar",
    icon: Store,
    label: "Mağazalar",
    submenu: [
      { path: "/stores", icon: Store, label: "Shopify Mağazaları" },
      { path: "/distribution", icon: ArrowLeftRight, label: "Mağaza Dağıtımı" },
      { path: "/marketplaces", icon: Globe, label: "Pazaryerleri" },
      { path: "/trendyol-setup", icon: Settings2, label: "Trendyol Kurulum" },
    ]
  },
  {
    path: "/siparisler",
    icon: ShoppingCart,
    label: "Siparişler",
    submenu: [
      { path: "/orders", icon: ShoppingCart, label: "Sipariş Yönetimi" },
      { path: "/shopify-orders", icon: ShoppingBag, label: "Shopify Siparişleri" },
      { path: "/marketplace-orders", icon: Globe, label: "Pazaryeri Siparişleri" },
      { path: "/order-sync", icon: ArrowLeftRight, label: "Sipariş Sync" },
    ]
  },
  {
    path: "/urunler",
    icon: Package,
    label: "Ürünler",
    submenu: [
      { path: "/products", icon: Package, label: "Ürün Yönetimi" },
      { path: "/shopify-products", icon: ShoppingBag, label: "Shopify Ürünleri" },
      { path: "/bundles", icon: Layers, label: "Asorti & Paketler" },
      { path: "/combinations", icon: Sparkles, label: "Kombin & Öneriler" },
    ]
  },
  { path: "/services", icon: Server, label: "Servisler" },
  { 
    path: "/nebim", 
    icon: Activity, 
    label: "Nebim",
    submenu: [
      { path: "/nebim/stok-ekle", icon: Plus, label: "Stok Ekle" },
      { path: "/nebim/stok-cikar", icon: Minus, label: "Stok Çıkar" },
      { path: "/nebim/urun-ac", icon: PackagePlus, label: "Ürün Aç" },
      { path: "/nebim/fotograf-ekle", icon: Image, label: "Fotoğraf Ekle" },
      { path: "/nebim/fiyat-degistir", icon: DollarSign, label: "Fiyat Değiştir" },
      { path: "/nebim/magaza-sevk", icon: Truck, label: "Diğer Mağaza Sevk" },
      { path: "/nebim/raporlar", icon: BarChart3, label: "Raporlar" },
    ]
  },
  {
    path: "/araclar",
    icon: Settings2,
    label: "Araçlar",
    submenu: [
      { path: "/price-rules", icon: DollarSign, label: "Fiyat Kuralları" },
      { path: "/advanced-rules", icon: AlertTriangle, label: "Gelişmiş Kurallar" },
      { path: "/incoterm", icon: Globe, label: "Incoterm" },
      { path: "/categories", icon: Layers, label: "Kategori Yönetimi" },
      { path: "/translation", icon: Globe, label: "Dil & Çeviri" },
      { path: "/webhooks", icon: Webhook, label: "Webhooks" },
      { path: "/scripts", icon: Code, label: "Scripts" },
      { path: "/metafields", icon: Database, label: "Metafields" },
    ]
  },
  { path: "/logs", icon: FileText, label: "Loglar" },
  { path: "/settings", icon: Settings, label: "Ayarlar" },
  { path: "/help", icon: HelpCircle, label: "Yardım", highlight: true },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState([]);

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const toggleSubmenu = (menuPath) => {
    setExpandedMenus(prev => 
      prev.includes(menuPath) 
        ? prev.filter(p => p !== menuPath)
        : [...prev, menuPath]
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        data-tour="sidebar"
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center px-4 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900 text-sm">Shopify-Nebim</h1>
                <p className="text-xs text-slate-500">Entegratör v3.0</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {navItems.map((item) => (
                <div key={item.path}>
                  {item.submenu ? (
                    <>
                      <button
                        onClick={() => toggleSubmenu(item.path.replace("/", ""))}
                        className={`sidebar-link w-full justify-between ${isActive(item.path) ? "sidebar-link-active" : ""}`}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <span className="flex items-center gap-3">
                          <item.icon className="w-5 h-5" />
                          <span>{item.label}</span>
                        </span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${expandedMenus.includes(item.path.replace("/", "")) ? "rotate-180" : ""}`} />
                      </button>
                      {expandedMenus.includes(item.path.replace("/", "")) && (
                        <div className="ml-4 mt-1 space-y-1">
                          {item.submenu.map((sub) => (
                            <NavLink
                              key={sub.path}
                              to={sub.path}
                              className={({ isActive: active }) =>
                                `sidebar-link text-sm ${isActive(sub.path) ? "sidebar-link-active" : ""}`
                              }
                              onClick={() => setSidebarOpen(false)}
                              data-testid={`nav-${sub.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                              <sub.icon className="w-4 h-4" />
                              <span>{sub.label}</span>
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <NavLink
                      to={item.path}
                      end={item.exact}
                      className={({ isActive: active }) =>
                        `sidebar-link ${(item.exact ? isActive(item.path, true) : isActive(item.path)) ? "sidebar-link-active" : ""} ${item.highlight ? "bg-blue-50 text-blue-600" : ""}`
                      }
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      data-tour={item.path === "/help" ? "help-link" : undefined}
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </NavLink>
                  )}
                </div>
              ))}
            </nav>
          </ScrollArea>

          {/* User Info */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-slate-600">
                  {user?.company_name?.charAt(0)?.toUpperCase() || "U"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {user?.company_name || "Kullanıcı"}
                </p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış Yap
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden mr-2"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>
            
            <div className="flex items-center text-sm text-slate-500">
              <span>Ana Sayfa</span>
              {location.pathname !== "/" && (
                <>
                  <ChevronRight className="w-4 h-4 mx-1" />
                  <span className="text-slate-900 font-medium">
                    {location.pathname.startsWith("/distribution") ? "Mağaza Dağıtımı" : navItems.find((item) => isActive(item.path) && !item.exact)?.label || "Dashboard"}
                  </span>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
